import { Delegate } from './delegate.js';
import { showToast } from './component-toast.js';

/**
 * PDP "Mix & save" (snippets/product-mix-save.liquid).
 *
 * The shopper picks products from a collection in a drawer; the tier they reach sets the discount shown.
 * The discount is display only: add to cart sends plain {id, quantity} lines and the automatic discounts in
 * Shopify admin do the pricing.
 *
 * The drawer is a stock <drawer-component>, which moves itself to <body> while open, so it is kept as a
 * reference instead of being looked up inside this element.
 */
const SEARCH_DEBOUNCE_MS = 150;
const MAX_PRODUCT_PAGES = 10;
const SEARCH_EVENTS = ['click', 'focusin', 'keydown', 'keyup', 'input'];

/* Same Discounty shape, wait and rule as product-quantity-discount.js, so both blocks agree on which
   variants are in a quantity campaign. Keep the two in sync if either changes.
   discount_type 1 = PERCENTAGE; operand_type 1 = COUNT_OF_ITEMS */
const PERCENTAGE = 1;
const COUNT_OF_ITEMS = 1;
const DISCOUNTY_POLL_MS = 100;
const DISCOUNTY_TIMEOUT_MS = 5000;

function whenDiscountyReady() {
  return new Promise((resolve) => {
    if (window.$Discounty) return resolve(window.$Discounty);

    const startedAt = Date.now();
    const poll = setInterval(() => {
      if (window.$Discounty || Date.now() - startedAt > DISCOUNTY_TIMEOUT_MS) {
        clearInterval(poll);
        resolve(window.$Discounty || null);
      }
    }, DISCOUNTY_POLL_MS);
  });
}

function hasQuantityDiscount(discounty, variantId) {
  const id = Number(variantId);
  if (!discounty || !id) return false;

  const variant = (discounty.products || [])
    .flatMap((product) => product.variants || [])
    .find((candidate) => Number(candidate.id) === id);
  const discount = (variant?.discounts || []).find(
    ({ operand_type, discount_type }) => operand_type === COUNT_OF_ITEMS && discount_type === PERCENTAGE
  );
  const tiers = (discount?.tiers || []).filter(
    ({ criteria, discount: value }) => Number(criteria) > 1 && Number(value) > 0
  );

  return tiers.length >= 2;
}

/**
 * Replaces [[key]] placeholders in a merchant-editable string.
 *
 * @param {string} template - Text with placeholders, e.g. "Add [[count]] more".
 * @param {Object} values - Placeholder values by key.
 * @returns {string} The filled text.
 */
function fillTemplate(template, values) {
  return Object.entries(values).reduce((text, [key, value]) => text.split(`[[${key}]]`).join(value), template || '');
}

/**
 * Lowercases text and strips accents, so "halsa" finds "hälsa".
 *
 * @param {string} text - Text to normalise.
 * @returns {string} Normalised text.
 */
function normalizeSearch(text) {
  return (text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * Formats cents in the store's money format.
 *
 * @param {number} cents - Amount in cents.
 * @returns {string} Formatted price.
 */
function formatMoney(cents) {
  return FoxTheme.Currency.formatMoney(Math.round(cents), FoxTheme.settings.moneyFormat);
}

/**
 * Price after a percentage discount, rounded per line as Shopify does.
 *
 * @param {number} price - Line price in cents.
 * @param {number} percent - Discount percentage.
 * @returns {number} Discounted price in cents.
 */
function discountedPrice(price, percent) {
  return price - Math.round((price * percent) / 100);
}

/**
 * Shows or hides an element for everyone, screen readers included.
 *
 * @param {HTMLElement|null} element - Element to toggle.
 * @param {boolean} isHidden - Whether the element should be hidden.
 */
function setHidden(element, isHidden) {
  if (!element) return;
  element.hidden = isHidden;
  element.setAttribute('aria-hidden', String(isHidden));
}

class MixSave extends HTMLElement {
  #discounty = null;
  #unsubscribeEligibility = null;
  #abortController = null;
  #delegate = null;
  #unsubscribeVariantChange = null;
  #config = null;
  #tiers = [];
  #maxItems = 0;
  #current = null;
  /** @type {Map<number, {variantId: number, price: number}>} Selected products, one variant each. */
  #selected = new Map();
  #drawer = null;
  /** @type {HTMLElement[]} Rows in the order they were fetched, current product first. */
  #baseItems = [];
  #listPromise = null;
  #listLoaded = false;
  #adding = false;
  #error = '';

  /**
   * Reads the config, preselects the current product and draws the initial state.
   */
  connectedCallback() {
    if (this.#abortController) return;

    this.#config = this.#readConfig();
    this.#drawer = this.querySelector('[data-mix-save-drawer]');
    if (!this.#config?.tiers?.length || !this.#drawer) return;

    this.#abortController = new AbortController();
    this.#tiers = [...this.#config.tiers].sort((a, b) => a.min - b.min);
    this.#maxItems = Math.max(this.#config.maxItems, this.#tiers.at(-1).min);
    this.#current = { ...this.#config.current };

    this.#resetSelection();
    this.#bindEvents();
    this.#render();
    this.#syncEligibility();
  }

  async #syncEligibility() {
    this.#discounty = await whenDiscountyReady();
    if (!this.isConnected) return;

    this.hidden = !hasQuantityDiscount(this.#discounty, this.#current?.variantId);

    this.#unsubscribeEligibility?.();
    this.#unsubscribeEligibility = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
      ({ data: { sectionId, variant } }) => {
        if (sectionId !== this.#config.sectionId) return;
        this.hidden = !hasQuantityDiscount(this.#discounty, variant?.id);
      }
    );
  }

  /**
   * Removes listeners, and the drawer too if it is still in <body> when the PDP re-renders.
   */
  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#delegate?.destroy();
    this.#unsubscribeVariantChange?.();
    this.#unsubscribeEligibility?.();
    this.#unsubscribeEligibility = null;

    const isDrawerOutside = this.#drawer && !this.contains(this.#drawer);
    if (isDrawerOutside) this.#drawer.remove();
  }

  /**
   * Parses the JSON config rendered by the snippet.
   *
   * @returns {Object|null} The config, or null when it is missing or invalid.
   */
  #readConfig() {
    try {
      return JSON.parse(this.querySelector('[data-mix-save-config]')?.textContent || 'null');
    } catch (error) {
      console.error('Mix & save: invalid config', error);
      return null;
    }
  }

  /**
   * Binds the card, drawer and variant listeners.
   */
  #bindEvents() {
    const { signal } = this.#abortController;
    const listenerOptions = { signal, once: true, passive: true };

    // Start loading on intent, so the list is usually there by the time the drawer has opened.
    this.querySelectorAll('[data-action="open-mix-save"]').forEach((opener) => {
      ['pointerenter', 'focus', 'touchstart'].forEach((type) => {
        opener.addEventListener(type, () => this.#loadProducts(), listenerOptions);
      });
    });

    this.#drawer.addEventListener('toggle', () => this.#handleDrawerToggle(), { signal });

    // On the drawer element itself, so the listeners survive its move to <body>.
    const handleSearch = FoxTheme.utils.debounce((event) => this.#filterList(event.target.value), SEARCH_DEBOUNCE_MS);
    this.#delegate = new Delegate(this.#drawer);
    this.#delegate.on('change', '[data-action="toggle-mix-save"]', (event, input) => this.#handleItemToggle(input));
    this.#delegate.on('input', '[data-action="search-mix-save"]', handleSearch);
    this.#delegate.on('focusin', '[data-action="toggle-mix-save"]', (event, input) => this.#revealRow(input));
    this.#delegate.on('click', '[data-action="add-mix-save"]', () => this.#addToCart());
    this.#isolateSearch(signal);

    this.#unsubscribeVariantChange = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
      (event) => this.#handleVariantChange(event)
    );
  }

  /**
   * Stops the search field's events at the drawer. Site search (Hello Retail) listens page-wide and would
   * take the shopper to the search page; listeners on the drawer itself still run.
   *
   * @param {AbortSignal} signal - Removes the listeners on disconnect.
   */
  #isolateSearch(signal) {
    SEARCH_EVENTS.forEach((type) => {
      this.#drawer.addEventListener(
        type,
        (event) => {
          const isSearchField = event.target instanceof Element && event.target.matches('[data-action="search-mix-save"]');
          if (!isSearchField) return;

          // Escape has to reach the drawer's own closer, which ModalComponent registers on document.
          // The stopPropagation below would swallow it; Enter and input stay isolated.
          if (event.key === 'Escape') return;

          event.stopPropagation();
          const isEnter = type === 'keydown' && event.key === 'Enter';
          if (isEnter) event.preventDefault();
        },
        { signal }
      );
    });
  }

  /**
   * Updates the drawer's aria-hidden and loads the products when it opens.
   */
  #handleDrawerToggle() {
    const isOpen = this.#drawer.open;
    this.#drawer.setAttribute('aria-hidden', String(!isOpen));
    if (!isOpen) return;

    this.#error = '';
    this.#clearSearch();
    this.#loadProducts().then(() => this.#showSelectionFirst());
    this.#render();
  }

  /**
   * Empties the search field and shows the whole list again. A query left from the last visit would
   * otherwise keep selected products hidden, where only the footer count gives them away.
   */
  #clearSearch() {
    const field = this.#drawer.querySelector('[data-action="search-mix-save"]');
    if (!field?.value) return;

    field.value = '';
    // Rows that have not been fetched yet are filtered by #renderList, which reads the field itself.
    if (this.#listLoaded) this.#filterList('');
  }

  /**
   * Scrolls a keyboard-focused row fully into view. Focus lands on the row's visually hidden checkbox,
   * which sits inside the row, so the browser alone scrolls only that part of the row into view.
   *
   * @param {HTMLInputElement} input - The focused row's checkbox.
   */
  #revealRow(input) {
    // Same condition as the focus ring, so a mouse click on a row never moves the list.
    if (!input.matches(':focus-visible')) return;

    input.closest('.mix-save-item')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  /**
   * Moves the selected rows to the top of the list and scrolls back up. Reopening the drawer otherwise
   * leaves the list at the top, where a selection made further down cannot be seen.
   */
  #showSelectionFirst() {
    if (!this.#listLoaded) return;

    const isSelected = (item) => this.#selected.has(Number(item.dataset.productId));
    const selectedItems = this.#baseItems.filter(isSelected);
    const otherItems = this.#baseItems.filter((item) => !isSelected(item));

    // Ordering off the fetched order, not the current one, keeps the rest of the list in place.
    this.#list.replaceChildren(...selectedItems, ...otherItems);
    const body = this.#drawer.querySelector('[data-mix-save-body]');
    if (body) body.scrollTop = 0;
  }

  /**
   * Selects only the current product, when it is in the collection and in stock.
   */
  #resetSelection() {
    this.#selected.clear();
    const { eligible, available, productId, variantId, price } = this.#current;
    if (eligible && available) this.#selected.set(productId, { variantId, price });
  }

  /**
   * Highest tier reached with this many products.
   *
   * @param {number} count - Number of selected products.
   * @returns {{min: number, percent: number}|null} The tier, or null below the first one.
   */
  #activeTier(count) {
    return this.#tiers.filter((tier) => tier.min <= count).at(-1) || null;
  }

  /**
   * Adds or removes a product when its checkbox changes.
   *
   * @param {HTMLInputElement} input - The row's checkbox.
   */
  #handleItemToggle(input) {
    const item = input.closest('.mix-save-item');
    const productId = Number(item.dataset.productId);
    const isFull = this.#selected.size >= this.#maxItems;

    if (input.checked && isFull) {
      input.checked = false;
      return;
    }

    if (input.checked) {
      this.#selected.set(productId, { variantId: Number(item.dataset.variantId), price: Number(item.dataset.price) });
    } else {
      this.#selected.delete(productId);
    }

    this.#error = '';
    this.#render();
  }

  /**
   * Keeps the current product's selection on the variant chosen on the PDP.
   *
   * @param {Object} event - The pubsub variantChange payload.
   * @param {Object} event.data - Contains sectionId and the new variant.
   */
  #handleVariantChange({ data } = {}) {
    const variant = data?.variant;
    const isOwnSection = data?.sectionId === this.#config.sectionId;
    if (!variant || !isOwnSection || !this.#current.eligible) return;

    const { productId } = this.#current;
    const wasSelected = this.#selected.has(productId);
    const hadAvailableVariant = this.#current.available;

    Object.assign(this.#current, {
      variantId: variant.id,
      price: variant.price,
      available: variant.available,
      meta: this.#variantMeta(variant),
    });

    // A page that opened on a sold-out variant had nothing preselected; select it now if there is room.
    const canPreselect = !hadAvailableVariant && this.#selected.size < this.#maxItems;
    if (variant.available && (wasSelected || canPreselect)) {
      this.#selected.set(productId, { variantId: variant.id, price: variant.price });
    } else if (!variant.available) {
      this.#selected.delete(productId);
    }

    this.#syncCurrentRow();
    this.#render();
  }

  /**
   * Row text for a variant: "100 ml, 179 kr", or just the price for single-variant products.
   *
   * @param {Object} variant - Shopify variant JSON.
   * @returns {string} The row text.
   */
  #variantMeta(variant) {
    const price = formatMoney(variant.price);
    const hasTitle = variant.title && variant.title !== 'Default Title';
    return hasTitle ? `${variant.title}, ${price}` : price;
  }

  /**
   * The drawer's product list.
   *
   * @returns {HTMLElement} The list element.
   */
  get #list() {
    return this.#drawer.querySelector('[data-mix-save-list]');
  }

  /**
   * All product rows in the drawer.
   *
   * @returns {HTMLElement[]} The rows.
   */
  get #items() {
    return Array.from(this.#list.querySelectorAll('.mix-save-item'));
  }

  /**
   * Loads the product rows once; later calls reuse the same request.
   *
   * @returns {Promise<void>} Resolves when the list is rendered.
   */
  #loadProducts() {
    this.#listPromise ||= this.#fetchProducts()
      .then((items) => this.#renderList(items))
      .catch((error) => {
        console.error('Mix & save: could not load products', error);
        this.#listPromise = null;
        this.#error = this.#config.strings.error;
        this.#render();
      })
      .finally(() => this.#drawer.querySelector('[data-mix-save-body]')?.setAttribute('aria-busy', 'false'));

    return this.#listPromise;
  }

  /**
   * Fetches the rows from sections/mix-save-products.liquid, following its pages.
   *
   * @returns {Promise<HTMLElement[]>} The rows, without duplicates.
   */
  async #fetchProducts() {
    const items = [];
    const seenProductIds = new Set();
    let url = this.#config.productsUrl;

    for (let page = 0; url && page < MAX_PRODUCT_PAGES; page++) {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const list = html.querySelector('[data-mix-save-items]');
      if (!list) break;

      list.querySelectorAll('.mix-save-item').forEach((item) => {
        const { productId } = item.dataset;
        if (seenProductIds.has(productId)) return;

        seenProductIds.add(productId);
        item.dataset.search = normalizeSearch(item.dataset.search);
        items.push(document.importNode(item, true));
      });
      url = list.dataset.nextUrl || null;
    }

    return items;
  }

  /**
   * Puts the rows in the drawer, with the current product first.
   *
   * @param {HTMLElement[]} items - The fetched rows.
   */
  #renderList(items) {
    const currentId = String(this.#current.productId);
    const currentItem = items.find((item) => item.dataset.productId === currentId);
    const otherItems = items.filter((item) => item !== currentItem);
    const orderedItems = currentItem ? [currentItem, ...otherItems] : items;
    const searchValue = this.#drawer.querySelector('[data-action="search-mix-save"]')?.value || '';

    this.#baseItems = orderedItems;
    this.#list.replaceChildren(...orderedItems);
    this.#listLoaded = true;
    this.#syncCurrentRow();
    this.#filterList(searchValue);
    this.#render();
  }

  /**
   * Points the current product's row at the PDP's variant. The section renders each product's first
   * available variant, which may be a different one.
   */
  #syncCurrentRow() {
    const { eligible, available, productId, variantId, price, meta } = this.#current;
    if (!eligible || !available) return;

    const item = this.#list.querySelector(`.mix-save-item[data-product-id="${productId}"]`);
    if (!item) return;

    item.dataset.variantId = variantId;
    item.dataset.price = price;
    const metaElement = item.querySelector('[data-mix-save-meta]');
    if (metaElement && meta) metaElement.textContent = meta;
  }

  /**
   * Shows only the rows matching the search, or the empty message.
   *
   * @param {string} query - The search text.
   */
  #filterList(query) {
    const search = normalizeSearch(query);
    let visibleCount = 0;

    this.#items.forEach((item) => {
      const matches = !search || item.dataset.search.includes(search);
      item.hidden = !matches;
      if (matches) visibleCount++;
    });

    const hasNoResults = this.#listLoaded && visibleCount === 0;
    setHidden(this.#drawer.querySelector('[data-mix-save-empty]'), !hasNoResults);
  }

  /**
   * Draws the whole state: progress, hints, summary, button and notice.
   */
  #render() {
    const strings = this.#config.strings;
    const count = this.#selected.size;
    const activeTier = this.#activeTier(count);
    const nextTier = this.#tiers.find((tier) => tier.min > count) || null;
    const topTier = this.#tiers.at(-1);

    this.#renderProgress(count);

    const hint = this.querySelector('[data-mix-save-hint]');
    if (hint) {
      const missingCount = nextTier ? nextTier.min - count : 0;
      hint.textContent = nextTier
        ? fillTemplate(strings.hintMore, { count: missingCount, percent: nextTier.percent })
        : fillTemplate(strings.hintUnlocked, { percent: topTier.percent });
    }

    const unlocked = this.#drawer.querySelector('[data-mix-save-unlocked]');
    if (unlocked) unlocked.textContent = fillTemplate(strings.drawerUnlocked, { percent: topTier.percent });
    setHidden(unlocked, Boolean(nextTier));

    this.#renderSummary(count, activeTier);
    this.#renderButton(count);
    this.#renderNotice(count);
    this.#syncInputs(count);
  }

  /**
   * Fills each tier's bar, in the card and in the drawer.
   *
   * @param {number} count - Number of selected products.
   */
  #renderProgress(count) {
    const fills = this.#tiers.map((tier, index) => {
      const from = index === 0 ? 0 : this.#tiers[index - 1].min;
      return Math.min(Math.max((count - from) / (tier.min - from), 0), 1);
    });

    const progressLists = new Set([
      ...this.querySelectorAll('.mix-save-progress'),
      ...this.#drawer.querySelectorAll('.mix-save-progress'),
    ]);
    progressLists.forEach((progress) => {
      progress.querySelectorAll('[data-mix-save-fill]').forEach((fill, index) => {
        fill.style.setProperty('--mix-save-fill', fills[index] ?? 0);
      });
    });
  }

  /**
   * Draws the footer summary: product count, original total and discounted total.
   *
   * @param {number} count - Number of selected products.
   * @param {{min: number, percent: number}|null} activeTier - Tier reached, if any.
   */
  #renderSummary(count, activeTier) {
    const strings = this.#config.strings;
    const prices = Array.from(this.#selected.values(), ({ price }) => price);
    const subtotal = prices.reduce((sum, price) => sum + price, 0);
    const total = activeTier
      ? prices.reduce((sum, price) => sum + discountedPrice(price, activeTier.percent), 0)
      : subtotal;

    const label = this.#drawer.querySelector('[data-mix-save-summary]');
    if (label) {
      if (count === 1) label.textContent = strings.summaryOne;
      else if (activeTier) label.textContent = fillTemplate(strings.summaryDiscount, { count, percent: activeTier.percent });
      else label.textContent = fillTemplate(strings.summaryMany, { count });
    }

    const compare = this.#drawer.querySelector('[data-mix-save-compare]');
    if (compare) compare.textContent = formatMoney(subtotal);
    setHidden(compare, !activeTier);

    const totalElement = this.#drawer.querySelector('[data-mix-save-total]');
    if (totalElement) totalElement.textContent = formatMoney(total);
  }

  /**
   * Sets the add button's text, and disables it below the first tier or while adding.
   *
   * @param {number} count - Number of selected products.
   */
  #renderButton(count) {
    const strings = this.#config.strings;
    const button = this.#drawer.querySelector('[data-action="add-mix-save"]');
    const buttonText = this.#drawer.querySelector('[data-mix-save-cta-text]');
    if (!button || !buttonText) return;

    const firstTier = this.#tiers[0];
    const isBelowFirstTier = count < firstTier.min;
    const missingCount = firstTier.min - count;
    const isDisabled = isBelowFirstTier || this.#adding;

    button.setAttribute('aria-disabled', String(isDisabled));
    buttonText.textContent = isBelowFirstTier
      ? fillTemplate(strings.ctaChooseMore, { count: missingCount })
      : fillTemplate(strings.ctaAdd, { count });
  }

  /**
   * Shows an add-to-cart error, or the maximum-reached note.
   *
   * @param {number} count - Number of selected products.
   */
  #renderNotice(count) {
    const notice = this.#drawer.querySelector('[data-mix-save-notice]');
    if (!notice) return;

    const isFull = count >= this.#maxItems;
    const maxMessage = isFull ? fillTemplate(this.#config.strings.maxReached, { count: this.#maxItems }) : '';
    const hasError = Boolean(this.#error);

    notice.textContent = this.#error || maxMessage;
    notice.classList.toggle('mix-save-drawer__notice--error', hasError);
    setHidden(notice, !notice.textContent);
  }

  /**
   * Ticks the selected rows and disables the others once the maximum is reached.
   *
   * @param {number} count - Number of selected products.
   */
  #syncInputs(count) {
    const isFull = count >= this.#maxItems;
    this.#items.forEach((item) => {
      const input = item.querySelector('[data-action="toggle-mix-save"]');
      if (!input) return;
      input.checked = this.#selected.has(Number(item.dataset.productId));
      input.disabled = isFull && !input.checked;
    });
  }

  /**
   * Adds the selected products to the cart in one request, then shows the toast.
   */
  async #addToCart() {
    const count = this.#selected.size;
    const activeTier = this.#activeTier(count);
    if (!activeTier || this.#adding) return;

    const strings = this.#config.strings;
    const button = this.#drawer.querySelector('[data-action="add-mix-save"]');
    this.#adding = true;
    this.#error = '';
    button?.classList.add('btn--loading');
    this.#render();

    // Let the cart components say which sections to re-render, as the other bundle features do.
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );

    const items = Array.from(this.#selected.values(), ({ variantId }) => ({ id: variantId, quantity: 1 }));
    const config = FoxTheme.utils.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.body = JSON.stringify({ items, sections, sections_url: window.location.pathname });

    try {
      const response = await fetch(FoxTheme.routes.cart_add_url, config);
      const state = await response.json();

      if (state.status) {
        this.#error = state.description || state.message || strings.error;
        return;
      }

      // The products are in the cart now, so a failed cart refresh must not show the shopper an error.
      const message = fillTemplate(strings.toast, { count, percent: activeTier.percent });
      this.#drawer.hide();
      showToast(message);
      this.#resetSelection();
      this.#render();
      await this.#refreshCart(state, sections);
    } catch (error) {
      console.error('Mix & save: add to cart failed', error);
      this.#error = strings.error;
    } finally {
      this.#adding = false;
      button?.classList.remove('btn--loading');
      this.#render();
    }
  }

  /**
   * Hands the new cart to the cart drawer and cart count. `/cart/add` can return `sections: null` (it does
   * under `shopify theme dev`) and the cart drawer throws on that, so the sections are fetched if missing.
   *
   * @param {Object} state - The `/cart/add` response.
   * @param {string[]} sectionIds - Sections the cart components asked for.
   */
  async #refreshCart(state, sectionIds) {
    try {
      const cart = await (await fetch(`${FoxTheme.routes.cart_url}.js`)).json();
      cart.sections = state.sections || (await this.#fetchSections(sectionIds));
      if (cart.sections) FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
      document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: state } }));
    } catch (error) {
      console.error('Mix & save: products added, but the cart could not be refreshed', error);
    }
  }

  /**
   * Renders sections through the Section Rendering API.
   *
   * @param {string[]} sectionIds - Section ids to render.
   * @returns {Promise<Object|null>} HTML by section id, or null.
   */
  async #fetchSections(sectionIds) {
    if (!sectionIds.length) return null;

    const url = `${window.location.pathname}?sections=${sectionIds.join(',')}`;
    const response = await fetch(url);
    return response.ok ? response.json() : null;
  }
}

if (!customElements.get('mix-save')) {
  customElements.define('mix-save', MixSave);
}
